/* eslint-disable quote-props, react/no-did-mount-set-state, prefer-template, react/jsx-no-bind, default-case */
import classnames from 'classnames';
import React from 'react';
import { GoalSource, ItemSource } from 'es6/models/session-config';
import Localization from 'es6/services/localization';

/**
 * Sets the state value to what was called and triggers
 * the onChange method.
 *
 * @param {String|Number} value
 */
function handleClick(value) {
  if (value !== this.state.value) {
    this.setState({ value });
    this.props.onChange.call(null, value);
  }
}

/**
 * Hide the underlying choice set when the popover opens.
 */
function handleOpen() {
  document.addEventListener('click', this._handleClose);
  this.setState({ isOpen: true });
}

/**
 * Show the underlying choice set when the popover closes.
 */
function handleClose() {
  document.removeEventListener('click', this._handleClose);
  this.setState({ isOpen: false });
}

/**
 * Helper to render a list item.
 *
 * @param {String} source
 * @param {String|Number} value
 */
function renderOption(source, value) {
  const sessionConfig = this.props.sessionConfig;
  const disabled = sessionConfig.isDisabled(source, value);
  const onClick = disabled ? null : handleClick.bind(this, value);
  const className = classnames('choice', {
    disabled,
    'selected': sessionConfig[source] === value,
  });
  const label = this.getDisplayLabel(value);

  return (
    <li key={value} className={className} onClick={onClick}>{label}</li>
  );
}

/**
 * Inheriting classes need to define a getDisplayLabel(), getSelectedValue()
 * and renderOptions() method.
 */
class DropdownBase extends React.Component {
  constructor(...args) {
    super(...args);

    // Keep a reference to bind and unbind from the document.
    this._handleClose = handleClose.bind(this);

    this.state = {
      isOpen: false,
      hasMounted: false,
      minWidth: 0,
      value: this.props.value,
    };
  }

  /**
   * Make sure the size of the menu and choice set are the same size.
   */
  componentDidMount() {
    const minWidth = this.refs.menu.clientWidth;
    this.setState({ hasMounted: true, minWidth });
  }

  /**
   * Update the state of the component if the value changes
   */
  componentWillReceiveProps(nextProps) {
    const { value } = nextProps;
    if (value) {
      this.setState({ isOpen: false, value });
    }
  }

  /**
   * Unbind the handle close method (in case it is bound when the node
   * is unmounted).
   */
  componentWillUnmount() {
    document.removeEventListener('click', this._handleClose);
  }

  render() {
    const className = classnames('session-config-dropdown', {
      'session-config-dropdown-open': this.state.isOpen,
    });
    const style = { minWidth: this.state.minWidth + 'px' };

    return (
      <div className={className} style={style}>
        <ul ref="menu" className="choice-set choice-set-vertical">
          {this.renderOptions()}
        </ul>

        <ul ref="placeholder" className="choice-set choice-set-expanded choice-set-placeholder">
          <li className="choice choice-dropdown selected" onClick={handleOpen.bind(this)}>
            {this.getDisplayLabel(this.getSelectedValue())}
          </li>
        </ul>
      </div>
    );
  }
}

DropdownBase.propTypes = {
  onChange: React.PropTypes.func.isRequired,
  sessionConfig: React.PropTypes.object.isRequired,
  value: React.PropTypes.any,
};

/**
 * Labels property should be a key for the corresponding size. The label
 * will default to the value if none is specified.
 *
 * Example:
 * 'sizes': [5, 10, 20]
 */
export class DropdownSessionSize extends DropdownBase {
  getDisplayLabel(value) {
    return value;
  }

  getSelectedValue() {
    return this.props.sessionConfig.sessionSize;
  }

  renderOptions() {
    return this.props.sizes.map(size => renderOption.call(this, 'sessionSize', size));
  }
}

DropdownSessionSize.propTypes = Object.assign({
  sizes: React.PropTypes.array.isRequired,
}, DropdownBase.propTypes);

export class DropdownItemSource extends DropdownBase {
  getDisplayLabel(value) {
    switch (value) {
      case ItemSource.ALL:
        return Localization.t('js_iknow.recommended');
      case ItemSource.REVIEW:
        return Localization.t('review-only');
      case ItemSource.NEW:
        return Localization.t('new-only');
    }

    return null;
  }

  getSelectedValue() {
    return this.props.sessionConfig.itemSource;
  }

  renderOptions() {
    return [
      renderOption.call(this, 'itemSource', ItemSource.ALL),
      renderOption.call(this, 'itemSource', ItemSource.REVIEW),
      renderOption.call(this, 'itemSource', ItemSource.NEW),
    ];
  }
}

export class DropdownGoalSource extends DropdownBase {
  getDisplayLabel(value) {
    switch (value) {
      case GoalSource.ALL:
        return Localization.t('js_iknow.all');
      case GoalSource.UNCOMPLETED:
        return Localization.t('in-progress');
      case GoalSource.COMPLETED:
        return Localization.t('completed');
    }

    return null;
  }

  getSelectedValue() {
    return this.props.sessionConfig.goalSource;
  }

  renderOptions() {
    return [
      renderOption.call(this, 'goalSource', GoalSource.ALL),
      renderOption.call(this, 'goalSource', GoalSource.UNCOMPLETED),
      renderOption.call(this, 'goalSource', GoalSource.COMPLETED),
    ];
  }
}
